import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Send, MessageCircle, Check, CheckCheck, Copy, Phone, ExternalLink, Link2 } from "lucide-react";
import { toast } from "sonner";
import type { Booking, ChatMessage } from "@/types";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

interface TherapistChatProps {
  booking: Booking | null;
  open: boolean;
  onClose: () => void;
  authHeaders: Record<string, string>;
}

const TherapistChat = ({ booking, open, onClose, authHeaders }: TherapistChatProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const chatLink = booking ? `${window.location.origin}/chat/${booking.id}?token=${booking.chat_token}` : "";
  const hasLineRelay = !!booking?.customer_line_user_id;

  const fetchMessages = useCallback(async () => {
    if (!booking?.id) return;
    try {
      const res = await axios.get(`${API}/chat/${booking.id}/messages`, { headers: authHeaders });
      setMessages(res.data.messages);
      await axios.post(`${API}/chat/${booking.id}/mark-read`, { reader_type: "therapist" }, { headers: authHeaders }).catch(() => {});
    } catch {
      // silently handle fetch failure
    }
  }, [booking?.id, authHeaders]);

  useEffect(() => {
    if (open && booking?.id) {
      fetchMessages();
      intervalRef.current = setInterval(fetchMessages, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [open, booking?.id, fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat/${booking.id}/therapist-send`, { message: newMessage.trim() }, { headers: authHeaders });
      setNewMessage("");
      if (res.data.line_sent) {
        toast.success("Message sent and delivered via LINE");
      }
      fetchMessages();
    } catch (err) {
    } finally {
      setSending(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(chatLink).then(() => toast.success("Chat link copied!"));
  };

  const sendChatLinkToCustomer = async () => {
    if (!booking?.id || sendingLink) return;
    setSendingLink(true);
    try {
      await axios.post(`${API}/chat/${booking.id}/send-chat-link`, {}, { headers: authHeaders });
      toast.success("Chat link sent to customer via LINE!");
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to send";
      if (detail.includes("does not have a LINE")) {
        toast.error("Customer doesn't have LINE linked. Share the chat link manually.");
      } else {
        toast.error(detail);
      }
    } finally {
      setSendingLink(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-md h-[80vh] flex flex-col p-0" aria-describedby={undefined}>
        <DialogHeader className="p-4 pb-2 border-b shrink-0">
          <DialogTitle className="text-sm">
            Chat with {booking?.name}
          </DialogTitle>
          <div className="text-xs text-gray-400 space-y-1">
            <div>{booking?.hotel_name}, Room {booking?.room_number}</div>
            {/* Contact info */}
            <div className="flex flex-wrap gap-2 mt-1">
              {booking?.contact_number && (
                <span className="inline-flex items-center gap-1 bg-gray-100 rounded px-1.5 py-0.5">
                  <Phone className="w-3 h-3" /> {booking.contact_number}
                </span>
              )}
              {booking?.line_id && (
                <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 rounded px-1.5 py-0.5">
                  LINE: {booking.line_id.startsWith("@") ? booking.line_id : `@${booking.line_id}`}
                </span>
              )}
              {booking?.whatsapp_number && (
                <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 rounded px-1.5 py-0.5">
                  WA: {booking.whatsapp_number}
                </span>
              )}
            </div>
            {/* Relay status + chat link */}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {hasLineRelay ? (
                <span className="text-green-600 text-[10px] font-medium">Messages auto-relay to LINE</span>
              ) : (
                <span className="text-orange-500 text-[10px] font-medium">LINE relay not available</span>
              )}
              <button
                onClick={copyLink}
                className="inline-flex items-center gap-1 text-[10px] text-blue-500 hover:text-blue-700 underline"
                data-testid="copy-chat-link"
              >
                <Copy className="w-3 h-3" /> Copy chat link
              </button>
              {hasLineRelay && (
                <button
                  onClick={sendChatLinkToCustomer}
                  disabled={sendingLink}
                  className="inline-flex items-center gap-1 text-[10px] text-green-600 hover:text-green-800 underline disabled:opacity-50"
                  data-testid="send-chat-link-line"
                >
                  <Link2 className="w-3 h-3" /> {sendingLink ? "Sending..." : "Send link via LINE"}
                </button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {messages.length === 0 && (
            <div className="text-center text-gray-400 text-sm mt-8">
              <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p>No messages yet</p>
              {!hasLineRelay && (
                <p className="text-xs mt-2 max-w-[250px] mx-auto">
                  Share the chat link with the customer so they can reply here.
                  <button onClick={copyLink} className="text-blue-500 underline ml-1">Copy link</button>
                </p>
              )}
              {hasLineRelay && (
                <p className="text-xs mt-1">Messages will be sent via LINE</p>
              )}
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} className={`flex mb-2 ${msg.sender_type === "therapist" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[80%] rounded-2xl px-3 py-2 ${
                  msg.sender_type === "therapist"
                    ? "bg-pink-500 text-white rounded-br-sm"
                    : "bg-gray-100 text-gray-800 rounded-bl-sm"
                }`}
              >
                <p className="text-xs font-medium opacity-70 mb-0.5">
                  {msg.sender_type === "therapist" ? "You" : msg.sender_name}
                  {msg.sent_via === "line" ? " (LINE)" : ""}
                </p>
                <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                {msg.sender_type === "therapist" && (
                  <div className="flex justify-end mt-0.5">
                    {msg.read_at ? (
                      <CheckCheck className="w-3.5 h-3.5 text-blue-200" data-testid="read-receipt-seen" />
                    ) : (
                      <Check className="w-3.5 h-3.5 text-white/50" data-testid="read-receipt-sent" />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="border-t p-3 flex gap-2 shrink-0">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            placeholder="Type a message..."
            className="flex-1 text-sm"
            data-testid="therapist-chat-input"
          />
          <Button onClick={handleSend} disabled={sending || !newMessage.trim()} size="sm" className="bg-pink-500 hover:bg-pink-600" data-testid="therapist-chat-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TherapistChat;
